import React from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../Page";
import { PrimaryButton, SecondaryButton } from "../Button";
import { InfoCallout, WarningCallout } from "../Callouts";
import { translationText } from "../i18n";
import type { CompanyBillingPlanSelectionPage as CompanyBillingPlanSelectionPageTypes } from "./types";
import { buildCompanyBillingPlanSelectionPageViewModel } from "./viewModel";

export { buildCompanyBillingPlanSelectionMode, buildCompanyBillingPlanSelectionPageViewModel } from "./viewModel";

export namespace CompanyBillingPlanSelectionPage {
  export type Mode = CompanyBillingPlanSelectionPageTypes.Mode;
  export type Interval = CompanyBillingPlanSelectionPageTypes.Interval;
  export type Plan = CompanyBillingPlanSelectionPageTypes.Plan;
  export type BillingOverview = CompanyBillingPlanSelectionPageTypes.BillingOverview;
  export type BillingTarget = CompanyBillingPlanSelectionPageTypes.BillingTarget;
  export type BillingTargetSelection = CompanyBillingPlanSelectionPageTypes.BillingTargetSelection;
  export type ActionTone = CompanyBillingPlanSelectionPageTypes.ActionTone;
  export type PlanCard = CompanyBillingPlanSelectionPageTypes.PlanCard;
  export type Action = CompanyBillingPlanSelectionPageTypes.Action;
  export type ConsequenceNotice = CompanyBillingPlanSelectionPageTypes.ConsequenceNotice;
  export type SelectionModeView = CompanyBillingPlanSelectionPageTypes.SelectionModeView;
  export type PageViewModel = CompanyBillingPlanSelectionPageTypes.PageViewModel;
  export type Props = CompanyBillingPlanSelectionPageTypes.Props;
}

export function CompanyBillingPlanSelectionPage(props: CompanyBillingPlanSelectionPage.Props) {
  const viewModel = buildCompanyBillingPlanSelectionPageViewModel(props);

  return (
    <Page title={props.title} size="medium" navigation={props.navigation} testId={props.testId}>
      <div className="p-8">
        <Header title={viewModel.pageTitle} subtitle={viewModel.pageSubtitle} />
        <SelectionModeView selection={viewModel.selection} />
      </div>
    </Page>
  );
}

function Header({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-8">
      <div>
        <div className="text-content-accent text-3xl font-extrabold">{title}</div>
        <div className="mt-2">{subtitle}</div>
      </div>
    </div>
  );
}

function SelectionModeView({ selection }: { selection: CompanyBillingPlanSelectionPage.SelectionModeView }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-10">
      {selection.errorMessage && <WarningCallout message={translationText(t("Plan action unavailable"))} description={selection.errorMessage} />}

      <BillingCycleSelector selection={selection} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {selection.cards.map((card) => (
          <PlanCard key={card.key} card={card} />
        ))}
      </div>

      {selection.consequenceNotice && <ConsequenceNotice notice={selection.consequenceNotice} />}

      <div className="flex justify-end">
        <ActionButton action={selection.continueAction} size="sm" />
      </div>
    </div>
  );
}

function BillingCycleSelector({ selection }: { selection: CompanyBillingPlanSelectionPage.SelectionModeView }) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto w-fit">
      <div className="mb-2 text-center text-[11px] font-medium uppercase tracking-[0.12em] text-content-dimmed">
        {t("Billing cycle")}
      </div>

      <div className="inline-flex items-center gap-1 rounded-xl border border-stroke-base bg-surface-base p-1 shadow-xs">
        <BillingCycleOption
          active={selection.selectedInterval === "monthly"}
          title={t("Monthly")}
          onClick={() => selection.onSelectInterval("monthly")}
        />
        <BillingCycleOption
          active={selection.selectedInterval === "yearly"}
          title={t("Yearly")}
          onClick={() => selection.onSelectInterval("yearly")}
        />
      </div>
    </div>
  );
}

function BillingCycleOption({
  active,
  title,
  onClick,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[
        "rounded-lg px-4 py-2 text-sm font-semibold transition",
        active
          ? "bg-brand-1 text-white-1 shadow-sm"
          : "text-content-dimmed hover:bg-surface-dimmed hover:text-content-accent",
      ].join(" ")}
      aria-pressed={active}
      onClick={onClick}
    >
      {title}
    </button>
  );
}

function PlanCard({ card }: { card: CompanyBillingPlanSelectionPage.PlanCard }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={[
        "rounded-lg border px-5 py-4 text-left transition",
        card.selected
          ? "border-brand-1 bg-blue-50/70 dark:bg-blue-900/30"
          : "border-stroke-base bg-surface-base hover:border-brand-1/50",
        card.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      ].join(" ")}
      onClick={card.onSelect}
      disabled={card.disabled}
      data-test-id={card.testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-content-accent text-xl font-extrabold">{card.title}</div>
          <div className="mt-1 text-content-dimmed">{card.priceLabel}</div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {card.selected && <span className="rounded-full bg-brand-1 px-2 py-1 text-xs font-semibold text-white-1">{t("Selected")}</span>}
        </div>
      </div>

      <div className="mt-4 space-y-2 text-sm text-content-dimmed">
        {card.detailLines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
    </button>
  );
}

function ConsequenceNotice({
  notice,
}: {
  notice: CompanyBillingPlanSelectionPage.ConsequenceNotice;
}) {
  return (
    <div className="space-y-3">
      {notice.tone === "warning" ? (
        <WarningCallout message={notice.message} description={notice.description} />
      ) : (
        <InfoCallout message={notice.message} description={notice.description} />
      )}

      {notice.rows.length > 0 && (
        <div className="rounded-lg border border-stroke-base bg-surface-base px-5 py-4">
          <DetailRows rows={notice.rows} />
        </div>
      )}
    </div>
  );
}

function DetailRows({ rows }: { rows: CompanyBillingPlanSelectionPage.ConsequenceNotice["rows"] }) {
  return (
    <div className="divide-y divide-stroke-base">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
          <div className="text-content-dimmed">{row.label}</div>
          <div className="text-right font-medium text-content-accent">{row.value}</div>
        </div>
      ))}
    </div>
  );
}

function ActionButton({
  action,
  size,
}: {
  action: CompanyBillingPlanSelectionPage.Action;
  size: "xs" | "sm";
}) {
  if (action.tone === "secondary") {
    return (
      <SecondaryButton size={size} onClick={action.onClick} disabled={action.disabled} loading={action.loading}>
        {action.label}
      </SecondaryButton>
    );
  }

  return (
    <PrimaryButton size={size} onClick={action.onClick} disabled={action.disabled} loading={action.loading}>
      {action.label}
    </PrimaryButton>
  );
}
